import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { Button } from "./ui/button";
import { Copy, Check, Share2, X, Twitter, Mail, MessageCircle, Link2, Users } from "lucide-react";
import { toast } from "sonner";
import { track } from "../lib/analytics";

/**
 * ShareDialog — universal share surface used from many places.
 * Modes:
 *   roadmap   — generates a /share/roadmap/:token link
 *   invite    — gets/creates the user's referral code
 *   streak    — shares "I'm on a {n}-day streak with Ascend Higher"
 */
export default function ShareDialog({ open, onClose, mode = "invite", streak = 0 }) {
  const [link, setLink] = useState("");
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [redemptions, setRedemptions] = useState(0);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        if (mode === "roadmap") {
          const r = await axios.post(`${API}/share/roadmap`);
          const url = `${window.location.origin}${r.data.share_url}`;
          setLink(url);
          track("share_roadmap_link_created", { token: r.data.token });
        } else if (mode === "invite") {
          const r = await axios.post(`${API}/referral/me`);
          setCode(r.data.code);
          setRedemptions(r.data.redemptions || 0);
          const url = `${window.location.origin}/?ref=${r.data.code}`;
          setLink(url);
        } else if (mode === "streak") {
          // No backend needed — just a static share
          setLink(`${window.location.origin}/?utm_source=streak_share`);
        }
      } catch (e) {
        toast.error("Couldn't load share link.");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, mode]);

  const text = mode === "streak"
    ? `I'm on a ${streak}-day streak working on myself with Ascend Higher — an AI life coach that actually remembers me. Try it free →`
    : mode === "roadmap"
    ? `Here's the life roadmap I'm building with Ascend Higher. AI life coach. Pretty wild.`
    : `I've been using Ascend Higher — an AI life coach that builds you a daily roadmap. Try it free with my link →`;

  const copy = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success("Link copied.");
    track("share_link_copied", { mode });
    setTimeout(() => setCopied(false), 2000);
  };

  const copyMessage = async () => {
    await navigator.clipboard.writeText(`${text}\n\n${link}`);
    toast.success("Message copied.");
    track("share_message_copied", { mode });
  };

  const tweet = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(link)}`;
    window.open(url, "_blank", "noopener");
    track("share_button_clicked", { channel: "twitter", mode });
  };

  const wa = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(`${text} ${link}`)}`;
    window.open(url, "_blank", "noopener");
    track("share_button_clicked", { channel: "whatsapp", mode });
  };

  const email = () => {
    const subject = mode === "roadmap" ? "My Ascend roadmap" : "Try this with me";
    const url = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`${text}\n\n${link}`)}`;
    window.location.href = url;
    track("share_button_clicked", { channel: "email", mode });
  };

  const native = async () => {
    if (!navigator.share) return copy();
    try {
      await navigator.share({ title: "Ascend Higher", text, url: link });
      track("share_button_clicked", { channel: "native", mode });
    } catch (e) {}
  };

  if (!open) return null;

  const titleByMode = {
    roadmap: "Share your roadmap",
    invite: "Invite a friend",
    streak: `Share your ${streak}-day streak`,
  };

  return (
    <div
      data-testid="share-dialog"
      className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-3xl bg-white border border-[#eadfc9] p-7 shadow-[0_30px_80px_-30px_rgba(15,23,36,0.4)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="font-mono-eyebrow text-[#486a8f] mb-1">SHARE</p>
            <h3 className="font-display text-[22px] text-[#0f1724] tracking-tight">
              {titleByMode[mode]}
            </h3>
          </div>
          <button
            data-testid="share-dialog-close"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-[#faf6ec] hover:bg-[#f2ecdc] inline-flex items-center justify-center text-[#6b7280]"
          >
            <X size={14} />
          </button>
        </div>

        {mode === "invite" && code && (
          <div className="mb-5 rounded-2xl bg-[#f7f3ea] border border-[#eadfc9] p-4">
            <p className="font-mono-eyebrow text-[#486a8f] mb-2">YOUR REFERRAL CODE</p>
            <div className="flex items-center justify-between">
              <code className="font-display text-[24px] text-[#0f1724] tracking-wide">{code}</code>
              <div className="inline-flex items-center gap-1.5 text-[#3e6b52]">
                <Users size={13} />
                <span className="font-body text-[12px]">{redemptions} joined</span>
              </div>
            </div>
          </div>
        )}

        <p className="font-body text-[14px] text-[#4b5563] leading-relaxed mb-4">
          "{text}"
          <button
            onClick={copyMessage}
            className="ml-2 text-[#486a8f] hover:underline font-medium text-[12px]"
          >
            copy text
          </button>
        </p>

        {/* Link */}
        <div className="flex items-center gap-2 rounded-xl bg-[#faf6ec] border border-[#eadfc9] p-2.5 mb-5">
          <Link2 size={14} className="text-[#6b7280] ml-1 shrink-0" />
          <input
            data-testid="share-link-input"
            readOnly
            value={loading ? "Loading…" : link}
            className="flex-1 bg-transparent font-mono text-[12px] text-[#2b3442] outline-none truncate"
          />
          <Button
            data-testid="share-copy-btn"
            onClick={copy}
            disabled={loading || !link}
            className="h-8 px-3 rounded-lg bg-[#486a8f] hover:bg-[#3a5a7c] text-white text-[12px] font-medium"
          >
            {copied ? <Check size={13} /> : <Copy size={13} className="mr-1.5" />}
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>

        {/* Social buttons */}
        <div className="grid grid-cols-4 gap-2">
          <ShareBtn onClick={native} icon={<Share2 size={15} />} label="Share" testid="share-native-btn" />
          <ShareBtn onClick={tweet} icon={<Twitter size={15} />} label="X / Twitter" testid="share-twitter-btn" />
          <ShareBtn onClick={wa} icon={<MessageCircle size={15} />} label="WhatsApp" testid="share-whatsapp-btn" />
          <ShareBtn onClick={email} icon={<Mail size={15} />} label="Email" testid="share-email-btn" />
        </div>

        <p className="font-body text-[11px] text-[#9aa3ae] mt-5 text-center">
          {mode === "roadmap"
            ? "Anyone with this link can view your roadmap (no personal info shared)."
            : mode === "invite"
            ? "Friends who join from your link help you grow your circle."
            : "Inspire someone to start their own."}
        </p>
      </div>
    </div>
  );
}

function ShareBtn({ onClick, icon, label, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="h-12 rounded-xl bg-[#faf6ec] hover:bg-[#f2ecdc] border border-[#eadfc9] inline-flex flex-col items-center justify-center gap-0.5 text-[#486a8f] transition-colors"
    >
      {icon}
      <span className="font-body text-[10px] text-[#2b3442]">{label}</span>
    </button>
  );
}
